import React, { useState } from 'react';
import { Mail, Lock, Eye, EyeOff } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useAuth } from '../../contexts/AuthContext';
import betanxtLogo from '../../assets/betanxt-blue-logo.png';

export function LoginPage() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [rememberMe, setRememberMe] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const navigate = useNavigate();
  const { login } = useAuth();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      const result = await login(email, password, rememberMe);

      if (result.success) {
        navigate('/ace');
      } else {
        setError(result.error?.message || 'Login failed');
      }
    } catch (err) {
      setError('An unexpected error occurred');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen w-full flex items-center justify-center px-4 relative overflow-hidden" style={{ backgroundColor: '#f7f8f9' }}>
      {/* Content */}
      <motion.div
        className="w-full max-w-[400px] flex flex-col relative z-10"
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6, delay: 0.3 }}
      >
        {/* Logo Section */}
        <motion.div
          className="flex flex-col items-center mb-8"
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.5, delay: 0.5 }}
        >
          <div className="flex justify-center items-center mb-2">
            <img
              src={betanxtLogo}
              alt="BetaNXT Logo"
              className="h-12 object-contain"
            />
          </div>
          <motion.p
            className="text-base font-medium leading-8 text-center"
            style={{ color: '#043243' }}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.5, delay: 0.7 }}
          >
            Powered by <span className="font-bold">OneNexus</span>
          </motion.p>
        </motion.div>

        {/* Login Card */}
        <motion.div
          className="bg-white rounded-xl p-8"
          style={{ boxShadow: '0 4px 24px 0 rgba(4, 50, 67, 0.10)' }}
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.6 }}
        >
          <h2 className="text-2xl font-bold text-center mb-6" style={{ color: '#043243' }}>
            Welcome
          </h2>

          {error && (
            <motion.div
              className="mb-4 p-3 bg-red-50 border border-red-300 rounded-lg"
              initial={{ opacity: 0, x: -10 }}
              animate={{ opacity: 1, x: 0 }}
            >
              <p className="text-sm text-red-600">{error}</p>
            </motion.div>
          )}

          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Username Input */}
            <div>
              <label htmlFor="email" className="block text-sm font-medium mb-2" style={{ color: '#73747a' }}>
                Username
              </label>
              <div className="flex items-center border border-gray-300 rounded-lg px-2 bg-white focus-within:border-teal-400 focus-within:ring-2 focus-within:ring-teal-400/20 transition-all duration-200">
                <Mail className="h-5 w-5 text-gray-400 ml-1" />
                <input
                  id="email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="admin@nexusfrontier.tech"
                  className="flex-1 border-0 bg-transparent px-3 py-2.5 text-base text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-0"
                  required
                />
              </div>
            </div>

            {/* Password Input */}
            <div>
              <label htmlFor="password" className="block text-sm font-medium mb-2" style={{ color: '#73747a' }}>
                Password
              </label>
              <div className="flex items-center border border-gray-300 rounded-lg px-2 bg-white focus-within:border-teal-400 focus-within:ring-2 focus-within:ring-teal-400/20 transition-all duration-200">
                <Lock className="h-5 w-5 text-gray-400 ml-1" />
                <input
                  id="password"
                  type={showPassword ? 'text' : 'password'}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="Enter your password"
                  className="flex-1 border-0 bg-transparent px-3 py-2.5 text-base text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-0"
                  required
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="p-1 text-gray-400 hover:text-gray-600 transition-colors rounded"
                  aria-label={showPassword ? 'Hide password' : 'Show password'}
                >
                  {showPassword ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
                </button>
              </div>
            </div>

            {/* Remember Me & Forgot Password */}
            <div className="flex items-center justify-between mt-2">
              <div className="flex items-center">
                <input
                  id="remember-me"
                  name="remember-me"
                  type="checkbox"
                  checked={rememberMe}
                  onChange={(e) => setRememberMe(e.target.checked)}
                  className="h-4 w-4 rounded border-gray-300 bg-white cursor-pointer"
                  style={{ accentColor: '#14b8a6' }}
                />
                <label htmlFor="remember-me" className="ml-2 block text-sm cursor-pointer" style={{ color: '#73747a' }}>
                  Remember me
                </label>
              </div>
              <div className="text-sm">
                <a href="#" className="font-medium transition-colors text-teal-600">
                  Forgot password?
                </a>
              </div>
            </div>

            {/* Continue Button */}
            <motion.button
              type="submit"
              disabled={loading}
              className="w-full h-[44px] rounded-lg text-base font-medium text-white shadow-sm focus:outline-none focus:ring-2 focus:ring-offset-2 transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed bg-gradient-to-r from-teal-500 to-cyan-500"
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
            >
              {loading ? 'Signing in...' : 'Continue'}
            </motion.button>
          </form>
        </motion.div>

        {/* Footer */}
        <motion.div
          className="mt-8 pt-4 flex items-center justify-center border-t border-gray-200"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.5, delay: 1 }}
        >
          <p className="text-xs" style={{ color: '#73747a' }}>
            © 2026 BetaNXT Inc and Nexus Frontier Tech Ltd. All rights reserved.
          </p>
        </motion.div>
      </motion.div>
    </div>
  );
}
